import type { GroomingBacklogItem, GroomingItemCreator } from "@/lib/api/grooming";
import { createdShort } from "@/lib/grooming/backlog-sort";
import { NEXT_STEP_WORDS, type NextStep } from "@/lib/grooming/plain-words";

/**
 * The Backlog's next step per row and the words around the author ask
 * (epic PI-SPEC-SW-E292, UX-PM-11 and UX-PM-06; frames D1 and D2). Pure: the
 * page passes in the signed-in person's e-mail and capabilities.
 */

/** The row fields the next step reads. */
export type NextStepRow = Pick<GroomingBacklogItem, "nextAction" | "authorAsk" | "createdBy">;

/**
 * True when the signed-in person created the item in ADO: the creator's
 * sign-in name equals their e-mail, ignoring case.
 */
export function wroteIt(createdBy: GroomingItemCreator | null | undefined, viewerEmail: string | null | undefined): boolean {
  const uniqueName = createdBy?.uniqueName?.trim().toLowerCase();
  const email = viewerEmail?.trim().toLowerCase();
  return Boolean(uniqueName && email && uniqueName === email);
}

/**
 * A row's next step: the API's next action, and for an item below the
 * grooming bar the asked state. Asked and the item changed since: Check the
 * reply. Asked and unchanged: Waiting on the author. Not asked: Fix it
 * yourself when the viewer wrote the item, else Ask the author.
 */
export function nextStepOf(row: NextStepRow, viewerEmail?: string | null): NextStep {
  switch (row.nextAction) {
    case "review-brief":
      return "review-brief";
    case "add-to-session":
      return "add-to-session";
    case "ready":
      return "ready-for-sprint";
    case "ask-author": {
      const ask = row.authorAsk ?? null;
      if (ask) return ask.changedSinceAsk ? "check-reply" : "waiting-on-author";
      return wroteIt(row.createdBy, viewerEmail) ? "fix-yourself" : "ask-author";
    }
  }
}

/** The capabilities that decide whether a next step is a button (from useCan / useCanAnywhere). */
export interface NextStepCapabilities {
  /** `manage-agenda` on the project: Review brief (posting the session's briefs) and Add to session. */
  manageAgenda: boolean;
  /** `post-single-brief` on the project: Ask the author and Check the reply. */
  postSingleBrief: boolean;
  /** `formulator` on any project: Fix it yourself. */
  formulator: boolean;
}

/** The capability each step needs to be an action; null for the steps that are always text. */
export const NEXT_STEP_NEEDS: Readonly<Record<NextStep, keyof NextStepCapabilities | null>> = {
  "review-brief": "manageAgenda",
  "add-to-session": "manageAgenda",
  "ask-author": "postSingleBrief",
  "check-reply": "postSingleBrief",
  "fix-yourself": "formulator",
  "waiting-on-author": null,
  "ready-for-sprint": null,
};

export interface NextStepView {
  step: NextStep;
  words: string;
  /** True when the viewer can take the step: a button. False: the same words as plain text. */
  actionable: boolean;
}

/** A row's next step as the viewer sees it: an action when they hold its capability, else plain text. */
export function nextStepView(
  row: NextStepRow,
  viewerEmail: string | null | undefined,
  capabilities: NextStepCapabilities
): NextStepView {
  const step = nextStepOf(row, viewerEmail);
  const needs = NEXT_STEP_NEEDS[step];
  return { step, words: NEXT_STEP_WORDS[step], actionable: needs !== null && capabilities[needs] };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole local calendar days from `then` to `now` (0 for the same day). */
function daysBetween(then: Date, now: Date): number {
  const a = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((b - a) / DAY_MS);
}

/** "today", "yesterday" or "6 days ago", in local days; "" when the date is missing or invalid. */
export function daysAgoWords(iso: string | null | undefined, now: Date = new Date()): string {
  const date = parse(iso);
  if (!date) return "";
  const days = Math.max(0, daysBetween(date, now));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${String(days)} days ago`;
}

/**
 * The asked part of a below-bar row's state line (frame D1): "author asked 6
 * days ago", or "not asked yet". Null for a row that is not below the bar.
 */
export function askedStateWords(
  row: Pick<GroomingBacklogItem, "stage" | "authorAsk">,
  now: Date = new Date()
): { asked: boolean; words: string } | null {
  if (row.stage !== "needs-information") return null;
  const ask = row.authorAsk ?? null;
  if (!ask) return { asked: false, words: "not asked yet" };
  const ago = daysAgoWords(ask.askedAt, now);
  return { asked: true, words: ago ? `author asked ${ago}` : "author asked" };
}

/**
 * The Author column's second line (frame D2): "Asked by Tony Truong · Oct 14",
 * "Not asked yet", or "Not asked yet · you wrote it". Names come from the
 * tracker and SpecTree; render as text only.
 */
export function askerLine(row: NextStepRow, viewerEmail?: string | null, now: Date = new Date()): string {
  const ask = row.authorAsk ?? null;
  if (!ask) return wroteIt(row.createdBy, viewerEmail) ? "Not asked yet · you wrote it" : "Not asked yet";
  const on = createdShort(ask.askedAt, now);
  const by = ask.askedByName ? `Asked by ${ask.askedByName}` : "Asked";
  return on ? `${by} · ${on}` : by;
}

/** "Item changed on Oct 18, after you asked" (frame D2), or null when the item did not change since the ask. */
export function changedSinceAskWords(row: Pick<GroomingBacklogItem, "authorAsk">, now: Date = new Date()): string | null {
  const ask = row.authorAsk ?? null;
  if (!ask?.changedSinceAsk) return null;
  const on = createdShort(ask.itemChangedAt, now);
  return on ? `Item changed on ${on}, after you asked` : "Item changed after you asked";
}

/** "<k> of <n> in place": the grooming rules the item meets, or null when the read carries no rule list. */
export function rulesInPlaceWords(row: Pick<GroomingBacklogItem, "readiness">): string | null {
  const criteria = row.readiness.criteria;
  if (!criteria || criteria.length === 0) return null;
  const present = criteria.filter((criterion) => criterion.present).length;
  return `${String(present)} of ${String(criteria.length)} in place`;
}

/**
 * The second line of a below-bar row in the filtered list (frame D2): the
 * amber "Item changed on <date>, after you asked", or the rules in place with
 * "no change since you asked" for an asked item. `changed` tells the page to
 * colour it amber.
 */
export function needsInfoDetail(
  row: Pick<GroomingBacklogItem, "authorAsk" | "readiness">,
  now: Date = new Date()
): { changed: boolean; text: string } {
  const changed = changedSinceAskWords(row, now);
  if (changed) return { changed: true, text: changed };
  const rules = rulesInPlaceWords(row);
  const tail = row.authorAsk ? "no change since you asked" : "not asked yet";
  return { changed: false, text: rules ? `${rules} · ${tail}` : tail.charAt(0).toUpperCase() + tail.slice(1) };
}

/**
 * The Needs information tile's note (frame D1): "2 authors asked · 2 not yet"
 * over the loaded below-bar rows, or null when there are none.
 */
export function authorsAskedNote(rows: readonly Pick<GroomingBacklogItem, "stage" | "authorAsk">[]): string | null {
  const below = rows.filter((row) => row.stage === "needs-information");
  if (below.length === 0) return null;
  const asked = below.filter((row) => row.authorAsk).length;
  const notYet = below.length - asked;
  return `${String(asked)} ${asked === 1 ? "author" : "authors"} asked · ${String(notYet)} not yet`;
}

/** The Ready to groom tile's note (frame D1): "2 briefs not posted", or null when every brief is posted. */
export function briefsNotPostedNote(rows: readonly Pick<GroomingBacklogItem, "nextAction">[]): string | null {
  const count = rows.filter((row) => row.nextAction === "review-brief").length;
  if (count === 0) return null;
  return `${String(count)} ${count === 1 ? "brief" : "briefs"} not posted`;
}
